/**
 * The tongs-claw lock routine bc-maul พยัคฆ์กดเหยื่อ (data/longMoves/bloodClaw.ts): one sound per beat sfx id. Self-made
 * synth, no samples. Wet tearing claws (bleed art): cloth rip + a low wet squelch, a bright scrape tick; the roar is a
 * low throat growl, short (<= 0.8 s); the body roll is cloth and a thud. Levels sit with sounds/longTempest.ts.
 */
import { registerSfx } from '../registry';
import { whoosh } from '../voices';

type K = Parameters<Parameters<typeof registerSfx>[2]>[0];

/** One rake: a cloth/flesh rip (band noise sweeping down), a wet low squelch, a bright scrape tick. */
function rake(k: K, at: number, f: number, vol: number): void {
  k.noise({ at, dur: 0.18, vol: 0.22 * vol, attack: 0.004, f: f * 1.6, f1: f * 0.6, sweep: 0.9, q: 2.2, color: 'pink' });
  k.noise({ at, dur: 0.07, vol: 0.2 * vol, attack: 0.001, type: 'highpass', f: 3800 });
  k.noise({ at: at + 0.03, dur: 0.16, vol: 0.16 * vol, attack: 0.01, type: 'bandpass', f: k.range(500, 800), q: 3, trem: [38, 0.7] });
  k.thump({ at: at + 0.04, f0: 180, f1: 70, dur: 0.1, vol: 0.12 * vol });
}

registerSfx('tigerChuff', { label: 'พยัคฆ์: พุ่งกด (หายใจฟึด)', cat: 'combat', gain: 0.6, vary: 0.04, gap: 0.3 }, (k) => {
  // The pounce: a hard nasal chuff, a cloth rush, a heavy landing on the foe.
  k.noise({ dur: 0.14, vol: 0.2, attack: 0.01, f: 1400, f1: 700, sweep: 0.8, q: 1.4, color: 'pink' });
  whoosh(k, { at: 0.05, dur: 0.3, f0: 400, f1: 1800, f2: 700, vol: 0.14, q: 1.4, peak: 0.5, color: 'pink' });
  k.thump({ at: 0.28, f0: 120, f1: 40, dur: 0.3, vol: 0.4 });
  k.noise({ at: 0.28, dur: 0.15, vol: 0.12, attack: 0.005, type: 'lowpass', f: 900, f1: 300, color: 'brown' });
});

registerSfx('growlLow', { label: 'พยัคฆ์: คำรามต่ำ (ขึ้นคร่อม)', cat: 'combat', gain: 0.6, vary: 0.05, gap: 0.4 }, (k) => {
  // Low throat rumble: a detuned saw pair with a fast wobble, breath noise through a low band.
  k.tone({ type: 'sawtooth', f: 62, f1: 55, dur: 0.6, vol: 0.09, attack: 0.15, vib: [26, 0.08] });
  k.tone({ type: 'sawtooth', f: 65, f1: 57, dur: 0.6, vol: 0.07, attack: 0.15, vib: [31, 0.06] });
  k.noise({ dur: 0.6, vol: 0.12, attack: 0.2, f: 300, f1: 200, q: 2, trem: [24, 0.6] });
});

registerSfx('clawRip', { label: 'กรงเล็บ: ฉีกซ้าย', cat: 'combat', gain: 0.6, vary: 0.05, gap: 0.2 }, (k) => rake(k, 0, 2200, 1));
registerSfx('clawRip2', { label: 'กรงเล็บ: ฉีกขวา', cat: 'combat', gain: 0.6, vary: 0.05, gap: 0.2 }, (k) => rake(k, 0, 1800, 1.05));
registerSfx('clawRip4', { label: 'กรงเล็บ: ฉีกรัว 4', cat: 'combat', gain: 0.55, vary: 0.04, gap: 0.3 }, (k) => {
  // Four rakes in a quick run, each a little lower and heavier.
  for (let n = 0; n < 4; n++) rake(k, n * 0.17 + k.range(0, 0.015), 2400 - n * 250, 0.8 + n * 0.1);
});

registerSfx('bodyRoll', { label: 'พยัคฆ์: พลิกร่าง', cat: 'combat', gain: 0.6, vary: 0.04, gap: 0.3 }, (k) => {
  // Cloth drag as the body is hooked over, then a flat thud as it lands face down.
  k.noise({ dur: 0.4, vol: 0.16, attack: 0.1, f: 1100, f1: 600, q: 1.1, trem: [14, 0.4], color: 'pink' });
  k.noise({ at: 0.05, dur: 0.3, vol: 0.07, attack: 0.1, type: 'highpass', f: 3500 });
  k.thump({ at: 0.4, f0: 140, f1: 50, dur: 0.22, vol: 0.4 });
  k.noise({ at: 0.4, dur: 0.12, vol: 0.12, attack: 0.003, type: 'lowpass', f: 700, f1: 250, color: 'brown' });
});

registerSfx('tigerRoar', { label: 'พยัคฆ์: คำราม', cat: 'combat', gain: 0.55, vary: 0.04, gap: 0.5 }, (k) => {
  // Short low roar (0.8 s): a saw stack with a fast growl wobble, rising a little then falling, a formant noise body.
  const body = k.shaper(2.5, k.gain(0.6));
  k.tone({ type: 'sawtooth', f: 90, f1: 120, dur: 0.8, vol: 0.14, attack: 0.08, vib: [28, 0.1], out: body });
  k.tone({ type: 'sawtooth', f: 94, f1: 70, dur: 0.8, vol: 0.1, attack: 0.1, vib: [34, 0.08], out: body });
  k.noise({ dur: 0.8, vol: 0.2, attack: 0.08, f: 700, f1: 380, q: 2.5, trem: [30, 0.5] });
  k.noise({ at: 0.02, dur: 0.6, vol: 0.06, attack: 0.1, f: 2200, f1: 1200, q: 1.8, trem: [30, 0.5] });
  k.thump({ at: 0.05, f0: 80, f1: 40, dur: 0.4, vol: 0.2 });
});

registerSfx('clawRipBig', { label: 'กรงเล็บ: ฉีกเฉียงขึ้น (ท่าจบ)', cat: 'combat', gain: 0.21, vary: 0.03, gap: 0.5, preview: 0.7 }, (k) => {
  // The upward two-claw tear: a wide wet rip, a cloth-splitting crackle, a heavy hit that throws the foe back.
  rake(k, 0, 1500, 1.6);
  rake(k, 0.04, 2000, 1.3);
  whoosh(k, { dur: 0.3, f0: 500, f1: 3600, f2: 2000, vol: 0.2, q: 1.8, peak: 0.6 });
  k.noise({ color: 'crackle', at: 0.05, dur: 0.3, vol: 0.35, attack: 0.002, type: 'highpass', f: 1800 });
  k.thump({ at: 0.1, f0: 130, f1: 36, dur: 0.5, vol: 0.55 });
  k.noise({ at: 0.1, color: 'brown', dur: 0.4, vol: 0.3, type: 'lowpass', f: 800, f1: 120 });
});
